import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLoaderData, useSubmit } from "react-router-dom";
import { useAuthStore, User } from "src/components/main/authStore";
import {
    round, 
    weekDays,
    numberReducer, 
    simpleStatusColorCode,
    MyDate, 
    isMobilePhone, 
    convertAMPM,
    reportWriterIds,
    getISOYearWeek
} from "src/components/common/functions";
import { getDatefromWeekNum, getDevice, InOut, LoaderData, JobTagForm } from "./types";
export default function TimeCard(){
    const formRef = useRef<HTMLFormElement>(null);
    const { data: initialData, reportsList, working, week, weeks } = useLoaderData() as LoaderData;
    const navigate = useNavigate();
    const user = useAuthStore((state) => state.user) as User;
    const [data, setData] = useState<JobTagForm>(initialData);
    const [isEdittingContent, setIsEdittingContent] = useState<boolean>(false);
    const [isEdittingTime, setIsEdittingTime] = useState<boolean>(false);
    const submit = useSubmit();
    const handlePunch = (): void => {
        if (isMobilePhone && user.outside !== "runner") {
            window.alert("Please use computer.");
            return;
        }
        if (!window.confirm(working ? "Punch Out" : "Punch In?")) return;
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const cordArray: [number, number, number] = [
                    position.coords.latitude,
                    position.coords.longitude,
                    position.coords.accuracy,
                ];
                const fd = new FormData();
                fd.append("cordArray", JSON.stringify(cordArray));
                fd.append("userAgent", navigator.userAgent);
                submit(fd, { method: "post", action: "Punch" });
            },
            (error) => alert(error.message),
            { enableHighAccuracy: true, timeout: 5000 }
        );
    };
    const handleSaveContent = (): void => {
        const inputs = document.getElementsByClassName("reportName") as HTMLCollectionOf<HTMLSelectElement>;
        for (const input of inputs) {
            if (!input.checkValidity()) { input.reportValidity(); return; }
        }
        const fd = new FormData();
        fd.append("week", week ?? "");
        fd.append("data", JSON.stringify(data));
        submit(fd, { method: "post", action: "UpdateContent" });
        setIsEdittingContent(false);
    };
    const handleSaveTime = (): void => {
        const form = formRef.current! as HTMLFormElement;
        if(!form.checkValidity()) return;
        const fd = new FormData();
        fd.append("week", week ?? "");
        fd.append("data", JSON.stringify(data));
        submit(fd, { method: "post", action: "UpdateTime" });
        setIsEdittingTime(false);
    };
    const handleSubmitWeek = (): void => {
        if (!week) return;
        if (!window.confirm("Are you sure to submit for the week?")) return;
        const fd = new FormData();
        fd.append("week", week);
        submit(fd, { method: "post", action: "Submit" });
    };
    const handleDownload = (): void => {
        if(!data.pdfId){
            window.alert("File Error.");
            return;
        }
        const fd = new FormData();
        fd.append("action", "downloadFile");
        fd.append("fileId", data.pdfId);
        fd.append("fileName", `Time Card ${user.userName} ${week}`);
        submit(fd, { method: "post", action: "Download" });
    };
    const updateDayInOut = (
        dayIndex: number,
        ioIndex: number,
        ioUpdater: (io: InOut) => InOut
    ): void => setData({
        ...data,
        form: data.form.map((day, dIdx) =>
            dIdx === dayIndex
                ? { ...day, inOut: day.inOut.map((io, iIdx) => (iIdx === ioIndex ? ioUpdater(io) : io)) }
                : day
        ),
    });
    useEffect(() => {
        setData(initialData);
    }, [initialData]);
    return (
        <div className="cssContent">
            <div className="text-danger fw-bold h4 mt-4">
                By using this app, I acknowledge California Meal and Rest Break Law.{" "}
                <a
                    href="https://www.shouselaw.com/ca/labor/wage-and-hour/meal-and-rest-breaks/"
                    target="_blank"
                    rel="noreferrer"
                >
                    Visit here for detail.
                </a>
            </div>
            <h4 className="d-flex align-items-center my-4">
                <label className="fw-bold me-2 mb-0">Week:</label>
                <select
                    className="form-select w-auto"
                    value={week}
                    onChange={e => navigate(`../${e.currentTarget.value}`, { relative: "path" })}
                >
                    {weeks.map((w) => <option key={w}>{w}</option>)}
                </select>
            </h4>
            {data.status === "Created" && data.week === getISOYearWeek() && <div className="my-2">
                <div className="d-flex align-items-center">
                    <button className="btn btn-info" onClick={handlePunch}>
                        {working ? "Punch Out" : "Punch In"}
                    </button>
                    <div className="fw-bold ms-2">{working ? "In Work" : "Out of Work"}</div>
                </div>
            </div>}
            <div className="mt-4">
                <h4 className="mb-4">
                    <span className="me-2">Status:</span>
                    <strong style={{ color: simpleStatusColorCode[data.status] }}>
                        {data.status}
                    </strong>
                </h4>
                {data.comments && <h4 className="mb-4">
                    <span className="me-2">Approver Comments:</span>
                    <strong style={{ color: simpleStatusColorCode[data.status] }}>
                        {data.comments}
                    </strong>
                </h4>}
                {data.pdfId && <h4 className="mb-4">
                    <span className="me-2">Form:</span>
                    <button
                        type = "button"
                        className="btn btn-primary"
                        onClick={handleDownload}
                    >
                        Download
                    </button>
                </h4>}
            </div>
            <form
                ref = {formRef}
                className="border rounded p-2 mb-3"
                onSubmit={handleSaveTime}
            >
                <div className="row">
                    {weekDays.map((renderedDay, renderedDayIndex) => (
                        <div className="col-lg" key={renderedDayIndex}>
                            <div className="h4 text-secondary">
                                {renderedDay} ({getDatefromWeekNum(week, renderedDayIndex)})
                            </div>
                            {data.form[renderedDayIndex].inOut.map((renderedIo, renderedIoIndex) => (
                                <div key={renderedIoIndex} className="p-2 mt-2 border rounded">
                                    <div>
                                        <span className={"fw-bold" + (renderedIo.m ? " text-danger" : "")}>In</span>:{" "}
                                        <span className={renderedIo.modifiedIn ? "text-decoration-line-through" : ""}>{renderedIo.in}</span>
                                        {!isEdittingTime && renderedIo.modifiedIn && (
                                            <>
                                                <span className="text-danger ms-2">
                                                    {(new MyDate("2021/05/21 " + renderedIo.modifiedIn)).ss()}
                                                </span>
                                                {renderedIo.modifiedInNotes && (
                                                    <div className="text-danger">{renderedIo.modifiedInNotes}</div>
                                                )}
                                            </>
                                        )}
                                        {renderedIo.inLocation && (
                                            <div className="mt-2">
                                                <span className={renderedIo.m ? "text-danger" : ""}>
                                                    <span className="fw-bold">Location:</span>
                                                    <a
                                                        className="ms-2"
                                                        href={`https://www.google.com/maps/search/${renderedIo.inLocation.slice(0, 2).join(",")}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        ({round(renderedIo.inLocation[0], 3)}, {round(renderedIo.inLocation[1], 3)}
                                                        {renderedIo.inLocation[2] ? `, ${renderedIo.inLocation[2]} meters` : null})
                                                    </a>
                                                </span>
                                            </div>
                                        )}
                                        {renderedIo.inUserAgent && (
                                            <div className="mt-2">
                                                <span className={renderedIo.m ? "text-danger" : ""}>
                                                    <span className="fw-bold me-2">Device:</span>
                                                    {getDevice(renderedIo.inUserAgent)}
                                                </span>
                                            </div>
                                        )}
                                        {isEdittingTime && (
                                            <>
                                                <input
                                                    type="time"
                                                    step="1"
                                                    className="form-control text-danger"
                                                    value={renderedIo.modifiedIn ?? ""}
                                                    min={
                                                        renderedIoIndex > 0
                                                            ? data.form[renderedDayIndex].inOut[renderedIoIndex - 1].modifiedOut ||
                                                            new Date("2021/05/21 " + data.form[renderedDayIndex].inOut[renderedIoIndex - 1].out).toLocaleTimeString("en-US", { hour12: false }) ||
                                                            ""
                                                            : undefined
                                                    }
                                                    max={renderedIo.modifiedOut || renderedIo.out}
                                                    onChange={e =>
                                                        updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({
                                                            ...io,
                                                            modifiedIn: e.currentTarget.value,
                                                        }))
                                                    }
                                                />
                                                <span
                                                    className="btn btn-warning"
                                                    onClick={() =>
                                                        updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({
                                                            ...io,
                                                            modifiedIn: "",
                                                        }))
                                                    }
                                                >
                                                    Clear
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center mt-2">
                                        <label
                                            htmlFor={`${renderedDayIndex} ${renderedIoIndex} break`}
                                            className="fw-bold text-nowrap mb-0"
                                        >
                                            10-min break {renderedIoIndex + 1}:
                                        </label>
                                        {isEdittingContent ? (
                                            <input
                                                id={`${renderedDayIndex} ${renderedIoIndex} break`}
                                                className="cssCheckBox ms-2"
                                                type="checkbox"
                                                checked={renderedIo.break ?? false}
                                                onChange={e =>
                                                    updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({
                                                        ...io,
                                                        break: e.currentTarget.checked,
                                                    }))
                                                }
                                            />
                                        ) : (
                                            <span className={`fw-bold text-${renderedIo.break ? "info" : "danger"} ms-2`}>
                                                {renderedIo.break ? "yes" : "no"}
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-2">
                                        <span className={"fw-bold" + (renderedIo.m ? " text-danger" : "")}>Out</span>:{" "}
                                        <span className={renderedIo.modifiedOut ? "text-decoration-line-through" : ""}>{renderedIo.out}</span>
                                        {!isEdittingTime && renderedIo.modifiedOut && (
                                            <>
                                                <span className="text-danger ms-2">
                                                    {(new MyDate("2021/05/21 " + renderedIo.modifiedOut)).ss()}
                                                </span>
                                                {renderedIo.modifiedOutNotes && (
                                                    <div className="text-danger">{renderedIo.modifiedOutNotes}</div>
                                                )}
                                            </>
                                        )}
                                        {renderedIo.outLocation && (
                                            <div className="mt-2">
                                                <span className={renderedIo.m ? "text-danger" : ""}>
                                                    <span className="fw-bold">Location:</span>
                                                    <a
                                                        className="ms-2"
                                                        href={`https://www.google.com/maps/search/${renderedIo.outLocation.slice(0, 2).join(",")}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        ({round(renderedIo.outLocation[0], 3)}, {round(renderedIo.outLocation[1], 3)}
                                                        {renderedIo.outLocation[2] ? `, ${renderedIo.outLocation[2]} meters` : null})
                                                    </a>
                                                </span>
                                            </div>
                                        )}
                                        {renderedIo.outUserAgent && (
                                            <div className="mt-2">
                                                <span className={renderedIo.m ? "text-danger" : ""}>
                                                    <span className="fw-bold me-2">Device:</span>
                                                    {getDevice(renderedIo.outUserAgent)}
                                                </span>
                                            </div>
                                        )}
                                        {isEdittingTime && (
                                            <>
                                                <input
                                                    type="time"
                                                    step="1"
                                                    className="form-control text-danger ms-2"
                                                    value={renderedIo.modifiedOut ?? ""}
                                                    min={
                                                        renderedIo.modifiedIn ||
                                                        new Date("2021/05/21 " + renderedIo.in).toLocaleTimeString("en-US", { hour12: false }) ||
                                                        ""
                                                    }
                                                    onChange={e =>
                                                        updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({
                                                            ...io,
                                                            modifiedOut: e.currentTarget.value,
                                                        }))
                                                    }
                                                />
                                                <span
                                                    className="btn btn-warning"
                                                    onClick={() =>
                                                        updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({
                                                            ...io,
                                                            modifiedOut: "",
                                                        }))
                                                    }
                                                >
                                                    Clear
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    {(renderedIo.duration === 0 || renderedIo.duration != null) && !isEdittingTime && (
                                        <div className="mt-2">
                                            <span className={renderedIo.m ? "text-danger" : ""}>
                                                <strong>Duration</strong>: {renderedIo.duration} h
                                            </span>
                                        </div>
                                    )}
                                    <div className="mt-2">
                                        <label
                                            htmlFor={`${renderedDayIndex} ${renderedIoIndex} notes`}
                                            className={"fw-bold" + (renderedIo.m ? " text-danger" : "") + " mb-0"}
                                        >
                                            Notes:
                                        </label>
                                        {isEdittingContent ? (
                                            <textarea
                                                id={`${renderedDayIndex} ${renderedIoIndex} notes`}
                                                className={"form-control" + (renderedIo.m ? " text-danger" : "")}
                                                value={renderedIo.notes ?? ""}
                                                onChange={e =>
                                                    updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({
                                                        ...io,
                                                        notes: e.currentTarget.value,
                                                    }))
                                                }
                                            />
                                        ) : (
                                            <div className={"d-block cssNotes" + (renderedIo.m ? " text-danger" : "")}>
                                                {renderedIo.notes}
                                            </div>
                                        )}
                                    </div>
                                    {reportWriterIds.includes(user.id) && (
                                        <>
                                            {isEdittingContent ? (
                                                <div className="mt-2">
                                                    {renderedIo.reports?.map((report, reportIndex) => (
                                                        <div key={reportIndex} className="p-2 mb-2 border rounded">
                                                            <label
                                                                htmlFor={`${renderedDayIndex} ${renderedIoIndex} ${reportIndex} report`}
                                                                className="fw-bold mb-0"
                                                            >
                                                                Report:
                                                            </label>
                                                            <select
                                                                id={`${renderedDayIndex} ${renderedIoIndex} ${reportIndex} report`}
                                                                required
                                                                className="form-control reportName"
                                                                value={report.id}
                                                                onChange={e =>
                                                                    updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({
                                                                        ...io,
                                                                        reports: io.reports!.map((r, ri) =>
                                                                            ri === reportIndex ? { ...r, id: e.currentTarget.value } : r
                                                                        ),
                                                                    }))
                                                                }
                                                            >
                                                                <option value="">Select</option>
                                                                {reportsList.map((r, ri) => (
                                                                    <option key={ri} value={r.value}>{r.label}</option>
                                                                ))}
                                                            </select>

                                                            <label className="fw-bold mb-0 mt-2">Start:</label>
                                                            <input
                                                                className="form-control"
                                                                type="time"
                                                                step="1"
                                                                min={reportIndex === 0 ? convertAMPM(renderedIo.modifiedIn || renderedIo.in) : undefined}
                                                                value={report.start}
                                                                onChange={e =>
                                                                    updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({
                                                                        ...io,
                                                                        reports: io.reports!.map((r, ri) =>
                                                                            ri === reportIndex ? { ...r, start: e.currentTarget.value } : r
                                                                        ),
                                                                    }))
                                                                }
                                                            />

                                                            <label className="fw-bold mb-0 mt-2">End:</label>
                                                            <input
                                                                className="form-control mb-2"
                                                                type="time"
                                                                step="1"
                                                                min={
                                                                    report.start < convertAMPM(renderedIo.modifiedOut || renderedIo.out)
                                                                        ? report.start
                                                                        : undefined
                                                                }
                                                                max={
                                                                    reportIndex === renderedIo.reports!.length - 1
                                                                        ? convertAMPM(renderedIo.modifiedOut || renderedIo.out)
                                                                        : undefined
                                                                }
                                                                value={report.end}
                                                                onChange={e =>
                                                                    updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({
                                                                        ...io,
                                                                        reports: io.reports!.map((r, ri) =>
                                                                            ri === reportIndex ? { ...r, end: e.currentTarget.value } : r
                                                                        ),
                                                                    }))
                                                                }
                                                            />
                                                            <span
                                                                className="btn btn-danger mt-2"
                                                                onClick={() =>
                                                                    updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({
                                                                        ...io,
                                                                        reports: [
                                                                            ...io.reports!.slice(0, reportIndex),
                                                                            ...io.reports!.slice(reportIndex + 1),
                                                                        ],
                                                                    }))
                                                                }
                                                            >
                                                                Delete
                                                            </span>
                                                        </div>
                                                    ))}
                                                    <div className="mt-2">
                                                        <span
                                                            className="btn btn-primary"
                                                            onClick={() =>
                                                                updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({
                                                                    ...io,
                                                                    reports: [...(io.reports ?? []), { id: "", start: "", end: "" }],
                                                                }))
                                                            }
                                                        >
                                                            Add Report
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : renderedIo.reports?.length ? (
                                                <div className="mt-2">
                                                    {renderedIo.reports.map((report, ri) => (
                                                        <li key={ri}>
                                                            {report.start} ~ {report.end}{" "}
                                                            <Link to={`/Reports/${report.id}`}>
                                                                {reportsList.find(item => item.value === report.id)?.label}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </div>
                                            ) : null}
                                        </>
                                    )}
                                    {isEdittingTime && (
                                        <div className="m-1 mt-2">
                                            {renderedIo.m && (
                                                <span
                                                    className="btn btn-danger m-1"
                                                    onClick={() => {
                                                        if (!window.confirm("Are you sure to delete this?")) return;
                                                        setData({
                                                            ...data,
                                                            form: data.form.map((day, dIdx) =>
                                                                dIdx === renderedDayIndex
                                                                    ? {
                                                                        ...day,
                                                                        inOut: [
                                                                            ...day.inOut.slice(0, renderedIoIndex),
                                                                            ...day.inOut.slice(renderedIoIndex + 1),
                                                                        ],
                                                                    }
                                                                    : day
                                                            ),
                                                        });
                                                    }}
                                                >
                                                    Delete This
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                            <div className="mt-2 d-block">
                                <label htmlFor={`${renderedDayIndex} summary`} className="fw-bold mb-0">Summary:</label>
                                {isEdittingContent ? (
                                    <textarea
                                        id={`${renderedDayIndex} summary`}
                                        className="form-control"
                                        value={data.form[renderedDayIndex].notes}
                                        onChange={e =>
                                            setData({
                                                ...data,
                                                form: data.form.map((day, dIdx) =>
                                                    dIdx === renderedDayIndex
                                                        ? { ...day, notes: e.currentTarget.value }
                                                        : day
                                                ),
                                            })
                                        }
                                    />
                                ) : (
                                    <div className="cssNotes d-block">{data.form[renderedDayIndex].notes}</div>
                                )}
                            </div>
                            <div className="mt-2">
                                <span className="fw-bold me-2">Total:</span>
                                {data.form[renderedDayIndex].inOut.length
                                    ? data.form[renderedDayIndex].inOut
                                        .map(io => io.duration ?? 0)
                                        .map(v => round(v, 2))
                                        .reduce(numberReducer, 0)
                                        .toFixed(2)
                                    : null}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="fw-bold h5 mt-2">
                    Week Total:{" "}
                    {data.form
                        .map(day =>
                            day.inOut.length
                                ? day.inOut.map(io => io.duration ?? 0).reduce(numberReducer, 0)
                                : 0
                        )
                        .reduce(numberReducer, 0)
                        .toFixed(2)}
                </div>
                {data.status !== "Approved" && (
                    <div className="text-right">
                        {!isEdittingTime && (
                            isEdittingContent ? (
                                <button type="button" className="btn btn-info" onClick={handleSaveContent}>
                                    Save Content
                                </button>
                            ) : (
                                <button type="button" className="btn btn-outline-info" onClick={() => setIsEdittingContent(true)}>
                                    Edit Content
                                </button>
                            )
                        )}
                        {/*
                        !isEdittingContent && (
                            isEdittingTime ? (
                                <button type="button" className="btn btn-info ms-2" onClick={handleSaveTime}>
                                    Save Time
                                </button>
                            ) : (
                                <button type = "button" className="btn btn-outline-warning ms-2" onClick={() => setIsEdittingTime(true)}>
                                    Edit Time
                                </button>
                            )
                        )
                        */}
                        {!isEdittingContent &&  !isEdittingTime && <button type="button" className="btn btn-primary ms-2" onClick={handleSubmitWeek}>
                            Submit For the Week
                        </button>}
                    </div>
                )}
            </form>
        </div>
    );
}
